import { useCallback, useState } from 'react';

import LaunchIcon from '@mui/icons-material/Launch';
import { Grid } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import MultipleDocumentUrlInputs from 'UI/components/MultipleDocumentUrlInputs/MultipleDocumentUrlInputs';
import Notes from 'UI/components/Notes/Notes';
import { navigateExternal, useT } from 'UI/lib';
import { getIsExternalUrl } from 'UI/lib/getUrlType';

import SelectedSkills from '../../../components/SelectedSkills';
import DateInput from '../../../DevelopmentPlan/AddMoreGoalsModals/components/DateInput';
import SubmitArea from '../../../DevelopmentPlan/AddMoreGoalsModals/components/SubmitArea';
import { useStatuses } from '../../useStatuses';
import { format } from 'date-fns';
import { AddProjectToPersonalDevelopmentDataInput, MeasureStatusEnum } from 'GQL/graphql';
import defaults from 'lodash/defaults';

interface AddProjectToPersonalPlanDialogProps {
  isEditMode?: boolean;
  isViewOnly?: boolean;
  onFinish: () => void;
  onSubmit: (data: AddProjectToPersonalDevelopmentDataInput) => Promise<void>;
  skillsYouWillLearn: any[];
  initialData: AddProjectToPersonalDevelopmentDataInput;
}

const fallbackFormValues: AddProjectToPersonalDevelopmentDataInput = {
  title: '',
  description: '',
  contactEmail: '',
  status: MeasureStatusEnum.Planned,
  startedAt: format(new Date(), 'yyyy-MM-dd'),
  capabilityUrl: '',
  documentUrl: '',
  notes: '',
  capabilityId: '',
  skillIds: [],
};

export const AddProjectToPersonalPlanDialog = ({
  isEditMode = false,
  isViewOnly = false,
  onFinish,
  onSubmit,
  skillsYouWillLearn,
  initialData,
}: AddProjectToPersonalPlanDialogProps) => {
  const t = useT();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const statuses = useStatuses();

  const [form, setForm] = useState(() => defaults(initialData, fallbackFormValues));

  const handleSubmit = useCallback(async () => {
    const newData = {
      ...form,
      skillIds: skillsYouWillLearn.map(skill => skill._id),
      startedAt: new Date(form.startedAt),
    };

    try {
      setIsSubmitting(true);
      await onSubmit(newData);
      onFinish();
    } finally {
      setIsSubmitting(false);
    }
  }, [form, skillsYouWillLearn, onSubmit, onFinish]);

  const setFormOnChange = useCallback((field, value) => setForm({ ...form, [field]: value }), [
    form,
  ]);

  const handleChange = (field, value) => setFormOnChange(field, value);

  const handleDocumentUrlChange = useCallback((documentUrl: string) => {
    setForm(form => ({ ...form, documentUrl }));
  }, []);

  const openLink = useCallback(() => {
    let navigateUrl = form.capabilityUrl;
    if (!getIsExternalUrl(navigateUrl)) {
      navigateUrl = 'http://' + navigateUrl;
    }

    navigateExternal(navigateUrl, true);
  }, [form.capabilityUrl]);

  const isSubmitButtonEnabled = form.title && form.startedAt && !isSubmitting;

  return (
    <Grid container>
      <Grid item xs={12}>
        <Grid container spacing={10}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              id="title"
              label={t('restrict:personal_development.recommendations_add_project_to_plan_title')}
              type="text"
              required
              value={form.title}
              error={!Boolean(form.title)}
              onChange={e => handleChange('title', e.target.value)}
              disabled
              data-test="project-title"
              multiline
              maxRows="2"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateInput
              id="startedAt"
              label={t(
                'restrict:personal_development.recommendations_add_project_to_plan_started_at'
              )}
              value={form.startedAt}
              onDateChange={handleChange}
              helperText={t('restrict:personal_development.started_at_date.help_text')}
              disabled={isViewOnly}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              variant="outlined"
              id="status"
              onChange={e => handleChange('status', e.target.value)}
              value={form.status}
              disabled={isViewOnly}
              data-test="project-status"
              label={t('restrict:pd.status_field')}
              InputLabelProps={{
                shrink: true,
              }}
            >
              {statuses.map(status => (
                <MenuItem key={status.value} value={status.value}>
                  {t(status.translationKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {skillsYouWillLearn && (
            <Grid item xs={12}>
              <InputLabel variant="standard">
                {t('restrict:personal_development.recommendations_add_project_to_plan_skills')}
              </InputLabel>
              <SelectedSkills
                isEditMode={!isViewOnly}
                skills={skillsYouWillLearn}
                withInteractiveSkills={false}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              variant="outlined"
              id="contactEmail"
              label={t(
                'restrict:personal_development.recommendations_add_project_to_plan_contact_email'
              )}
              InputLabelProps={{
                shrink: true,
              }}
              value={form.contactEmail}
              type="email"
              onChange={e => handleChange('contactEmail', e.target.value)}
              disabled={isViewOnly}
              data-test="project-contact-email"
              multiline
              maxRows="2"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              variant="outlined"
              id="capabilityUrl"
              label={t(
                'restrict:personal_development.recommendations_add_project_to_plan_capability_url'
              )}
              value={form.capabilityUrl}
              placeholder="https://..."
              type="url"
              onChange={e => handleChange('capabilityUrl', e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isViewOnly && form.capabilityUrl && (
                      <IconButton>
                        <LaunchIcon onClick={() => openLink()} />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={isViewOnly}
              data-test="project-capability-url"
              multiline
              maxRows="2"
            />
          </Grid>

          <Grid item xs={12}>
            <MultipleDocumentUrlInputs
              // Not the best practice to pass undefined, but i got confused by the component's props
              // so this is a workaround to avoid TS errors
              setIsValidExternalUrl={undefined}
              mergeUrls={handleDocumentUrlChange}
              isDisabled={isViewOnly}
              urls={form.documentUrl}
              label={t(
                'restrict:personal_development.recommendations_add_project_to_plan_document_url'
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Notes
              value={form.description}
              label={t(
                'restrict:personal_development.recommendations_add_project_to_plan_description'
              )}
              placeholder={t(
                'restrict:personal_development.recommendations_add_project_to_plan_description_placeholder'
              )}
              disabled={true}
              data-test="project-description"
            />
          </Grid>

          <Grid item xs={12}>
            <Notes
              value={form.notes}
              onValueChange={value => handleChange('notes', value)}
              label={t('restrict:personal_development.recommendations_add_project_to_plan_notes')}
              disabled={isViewOnly}
              data-test="project-notes"
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} mt={20}>
        <SubmitArea
          isDisabled={isViewOnly}
          onFinish={onFinish}
          isSubmitButtonEnabled={isSubmitButtonEnabled}
          buttonLabel={isEditMode ? t('restrict:pd.update_goals.save_button') : t('common:add')}
          tooltipLabel={t(
            'restrict:pd.add_further_goals.add_manually.submit_button_disabled.tooltip'
          )}
          onSubmit={handleSubmit}
        />
      </Grid>
    </Grid>
  );
};
